<template>
  <modal name="make-offer" class="make-offer" width="95%" height="auto" :scrollable="true">
    <LoadingSpinner v-if="loader" />
    <div v-else class="make-offer-modal">
      <!-- Close Button -->
      <button class="btn-close" @click="hide('make-offer')">
        <span class="mdi mdi-alpha-x-circle"></span>
      </button>

      <!-- Header -->
      <h3 class="modal-title">{{ $t("action.makeAnOffer") }}</h3>
      <!-- Total Price -->
      <div class="total-section">
        <strong>{{ $t("attr.total") }}:</strong>
        <span class="total-price">
          {{ totalPrice | currency(" DA", 0, { symbolOnLeft: false }) }}
        </span>
      </div>

      <div class="row mb-4">
        <div class="col-3 align-items-stretch d-flex" v-for="product in products" :key="product.id">
          <ProductMini :product="product.product" />

        </div>
      </div>


      <!-- Offer Options -->
      <div class="offer-options">
        <div v-for="(option, index) in offerOptions" :key="index" class="offer-option"
          :class="{ selected: selectedOption === index }" @click="selectOption(index)">
          <strong>
            {{ option.price | currency(" DA", 0, { symbolOnLeft: false }) }}

          </strong>
          <small>{{ option.label }}</small>
        </div>

        <!-- Custom Option -->
        <div class="offer-option" :class="{ selected: selectedOption === 'custom' }" @click="selectOption('custom')">
          <strong>{{ $t("sentence.other") }}</strong>
          <small>{{ $t("sentence.proposePrice") }}</small>
        </div>
      </div>

      <!-- Custom Input -->
      <div v-if="selectedOption === 'custom'" class="custom-input">
        <input v-model="customPrice" type="number" min="1" step="0.01" class="form-control"
          placeholder="Entrez votre prix" />
        <p v-if="customPrice && !isCustomPriceRounded" class="price-warning">
          ⚠️ {{ $t("sentence.customPriceNotRounded") }}
        </p>
      </div>

      <!-- Confirm Button -->
      <button class="btn-propose" @click="sendOffer" :disabled="loader">
        {{ buttonLabel }}
      </button>

    </div>
  </modal>
</template>

<script>
import config from "@/config";
import Helper from '@/services/helper';
import LoadingSpinner from "@/components/templates/LoadingSpinner.vue";

import ProductMini from '@/components/templates/ProductMini.vue';
export default {
  name: "MakeAnOfferModal",
  components: {
    LoadingSpinner,
    ProductMini,
  },
  props: {
    products: {
      type: Array,
      default: () => [],
      validator: (value) => {
        return Array.isArray(value)&&value.every(item => typeof item==='object');
      }
    },
    conversationId: {  // Add this new prop
      type: Number,
      required: false,
      default: null
    },
  },
  data() {
    return {
      selectedOption: null,
      customPrice: "",
      loader: false,
      offerOptions: [
        { discount: 0.05, label: "5% de réduction", price: 0 },
        { discount: 0.1, label: "10% de réduction", price: 0 },
      ],
      storageUrl: config.storageUrl,
    };
  },
  computed: {
    isLoggedIn() {
      return this.$store.getters.isLoggedIn;
    },
    offer_id() {
      return this.products[0].offer_id;
    },
    totalPrice() {
      return this.products.reduce((sum, item) => {
        return sum+this.calculateItemPrice(item);
      }, 0);
    },
    displayPrice() {
      if (this.selectedOption===null) return Helper.roundToHundreds(this.totalPrice);

      if (this.selectedOption==="custom") {
        return this.customPrice
          ? Helper.roundToHundreds(parseFloat(this.customPrice))
          :Helper.roundToHundreds(this.totalPrice);
      }

      return this.offerOptions[this.selectedOption].price;
    },
    isCustomPriceRounded() {
      if (!this.customPrice) return true;
      const price=parseFloat(this.customPrice);
      const rounded=Helper.roundToHundreds(price);
      return price===rounded;
    },
    buttonLabel() {
      if (!this.displayPrice) {
        return this.$t('action.proposer');
      }

      return `${this.$t('action.confirm')} ${this.$options.filters.currency(
        this.displayPrice,
        ' DA',
        0,
        { symbolOnLeft: false }
      )}`;
    }
  },
  mounted() {
    this.calculateOfferPrices();
  },
  watch: {
    products: {
      immediate: true,
      deep: true,
      handler() {
        this.resetOfferState();
        this.calculateOfferPrices();
      }
    },
    totalPrice() {
      this.calculateOfferPrices();
    }
  },
  methods: {
    hide(modal) {
      this.$modal.hide(modal);
    },
    resetOfferState() {
      this.selectedOption=null;
      this.customPrice="";
    },
    getProductName(item) {
      const product=item.product||item;
      return product.name||'';
    },
    getProductImage(item) {
      const product=item.product||item;
      if (!product||!product.pictures) return '';

      const picture=Array.isArray(product.pictures)
        ? product.pictures[0]
        :product.pictures;

      return this.storageUrl+picture;
    },
    calculateItemPrice(item) {
      const product=item.product||item;
      const quantity=item.quantity||1;

      if (!product||typeof product!=="object") return 0;

      let unitPrice=0;
      if (product?.sales_type==="fixed") {
        unitPrice=parseFloat(product.price||product.fixed_price)||0;
      } else {
        const lowestPrice=parseFloat(product.lowest_price)||0;
        unitPrice=lowestPrice+lowestPrice*0.16;
      }

      return unitPrice*quantity;
    },
    calculateOfferPrices() {
      if (!this.totalPrice||this.totalPrice===0) return;

      this.offerOptions.forEach((opt) => {
        const raw=this.totalPrice*(1-opt.discount);
        opt.price=Helper.roundToHundreds(raw);
      });
    },
    selectOption(index) {
      this.selectedOption=index;
    },
    sendOffer() {
      if (!this.isLoggedIn) {
        this.$router.push({
          name: "login"
        });
        return;
      }

      if (!this.products||this.products.length===0) {
        this.$notify({
          group: "error",
          title: "Erreur",
          text: "Aucun produit sélectionné.",
          type: "error",
        });
        return;
      }

      const offerPrice=parseFloat(this.displayPrice);
      const minPrice=parseFloat(this.totalPrice)*0.6;

      if (!offerPrice||offerPrice<=0) {
        this.$notify({
          group: "error",
          title: "Erreur",
          text: "Veuillez mettre un prix valide !",
          type: "error",
        });
        return;
      }

      if (offerPrice<minPrice) {
        this.$notify({
          group: "error",
          title: "Offre trop basse",
          text: "Votre offre n'est pas assez élevée.",
          type: "error",
        });
        return;
      }

      // Prepare single offer data with total prices
      const offerData={
        conversation_id: this.conversationId,
        price: Helper.roundToHundreds(offerPrice),
        old_price: Helper.roundToHundreds(this.totalPrice),
        quantity: null, // null for multi-product offers
        text: `${Helper.roundToHundreds(offerPrice)}`,
      };

      // For single product offers without conversation, include product_id
      if (!this.conversationId&&this.products.length===1) {
        const product=this.products[0].product||this.products[0];
        offerData.product_id=product.id;
        offerData.quantity=this.products[0].quantity||1;
      }

      this.loader=true;

      // Send ONE API request for all products
      this.$axios({
        url: "offers/create",
        method: "POST",
        data: offerData,
      })
        .then((response) => {
          this.loader=false;

          if (response.data&&response.data.success) {
            this.$notify({
              group: "success",
              title: "Bravo !",
              text: this.products.length>1
                ? "Votre offre groupée a été envoyée avec succès !"
                :"Votre offre a été envoyée avec succès !",
              type: "success",
            });

            // Emit ONCE for the entire offer
            this.$emit("offer-sent", {
              conversation_id: this.conversationId,
              offer_id: response.data.data?.id,
              product_count: this.products.length
            });

            this.hide("make-offer");

            this.$router.push({ name: 'messenger' });
          } else {
            this.$notify({
              group: "error",
              title: "Erreur",
              text: "Une erreur s'est produite lors de l'envoi de l'offre.",
              type: "error",
            });
          }
        })
        .catch((err) => {
          this.loader=false;
          if (err.response&&err.response.status===403) {
            this.show("login-user");
          } else {
            this.$notify({
              group: "error",
              title: "Erreur",
              text:
                (err.response&&this.$t(err.response.data.message))||
                "Une erreur s'est produite.",
              type: "error",
            });
          }
        });
    },
  },
};
</script>

<style scoped>
.make-offer-modal {
  padding: 1.5rem;
  max-width: 600px;
  margin: auto;
  background: white;
  border-radius: 16px;
  font-family: "Inter", sans-serif;
  color: var(--color-3);
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.modal-title {
  text-align: center;
  font-weight: 600;
  color: var(--color-3);
  margin-bottom: 1rem;
}

.product-img {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  object-fit: cover;
}

.product-name {
  font-size: 1rem;
  margin: 0;
  color: var(--color-2);
}

.product-price {
  font-size: 0.9rem;
  color: var(--color-7);
}

.offer-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 8px;
  margin-bottom: 1rem;
}

.offer-option {
  text-align: center;
  border: 1px solid #d0dada;
  border-radius: 8px;
  padding: 10px 5px;
  cursor: pointer;
  transition: 0.2s;
}

.offer-option:hover {
  border-color: var(--color-nz);
}

.offer-option.selected {
  border: 2px solid var(--color-nz);
  background: #f1fdfd;
}

.offer-option strong {
  display: block;
  color: var(--color-1);
  font-size: 1rem;
}

.offer-option small {
  color: var(--color-6);
  font-size: 0.8rem;
}

.custom-input {
  text-align: center;
  margin-bottom: 1rem;
}

.custom-input .form-control {
  width: 100%;
  border-radius: 8px;
  border: 1px solid var(--color-c);
  padding: 8px;
}

.custom-message textarea {
  width: 100%;
  border-radius: 8px;
  border: 1px solid var(--color-c);
  padding: 8px;
  margin-bottom: 1rem;
  resize: none;
}

@media (max-width: 768px) {
  .make-offer-modal {
    padding: 1rem;
    max-width: 90%;
    max-height: 85vh;
  }

  .product-img {
    width: 80px;
    height: 80px;
  }

  .offer-options {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 480px) {
  .make-offer-modal {
    padding: 0.8rem;
    max-height: 80vh;
    overflow-y: auto;
  }

  .modal-title {
    font-size: 1.1rem;
  }

  .offer-options {
    grid-template-columns: 1fr;
  }

  .offer-option strong {
    font-size: 0.95rem;
  }
}
</style>